import React, { useState } from 'react';
import { Info } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'shadcn/components/ui/Dialog';

export function ItemMasterBulkUploadInstructionsDialog() {
    const [open, setOpen] = useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-fit px-2 shrink-0 [&_svg]:size-5"
                    aria-label="Bulk upload instructions"
                >
                    More information <Info className="h-5 w-5" aria-hidden="true" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>Bulk upload instructions</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm leading-relaxed">
                    <p>Upload a CSV to bulk create or update item masters. Rows are validated before you can save.</p>
                    <p>
                        <b>Limit:</b> 1500 rows per upload.
                    </p>
                    <div>
                        <p className="font-semibold">Actions</p>
                        <p className="mt-1">
                            The <b>Action</b> column is required on every row. Set it to <b>add</b> or <b>update</b> to
                            indicate what you want to do.
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                            <li>
                                <b>add</b> — Create a new item.
                            </li>
                            <li>
                                <b>update</b> — Change an existing item identified by External SKU or Internal Name.
                                Include only the columns you want to change.
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">Taxonomy</p>
                        <p className="mt-1">
                            For <b>add</b> rows, only the deepest category is required for an item. Higher level
                            categories can be auto-populated from it:
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                            <li>
                                <b>Produce</b> — Item Category Four
                            </li>
                            <li>
                                <b>All other items</b> — Item Category Three
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">Customer Guarantee Days (CGD)</p>
                        <p className="mt-1">
                            CGD is set per company source. Required for <b>add</b>; optional on <b>update</b>{' '}
                            (leave blank for no change). Values pair with the <b>Company Sources</b> column by order.
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                            <li>
                                <b>Single value</b> (e.g. <b>14</b>) — applies that CGD to every company source on
                                the row.
                            </li>
                            <li>
                                <b>Comma-separated list</b> (e.g. <b>4, 9</b>) — must have the same count as Company
                                Sources. The 1st number maps to the 1st company source, the 2nd to the 2nd, and so on.
                                Example: Company Sources <b>misfits_market, blue_apron</b> with CGD <b>4, 9</b> sets
                                MM = 4 and Blue Apron = 9.
                            </li>
                        </ul>
                    </div>
                    <p>
                        <b>Clearing fields on update:</b> use &quot;null&quot; in a cell to remove that value.
                    </p>
                    <div>
                        <p className="font-semibold">Template markers</p>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                            <li>* — Required for the row&apos;s action</li>
                            <li>(*) — Required when applicable (depends on action or other columns)</li>
                        </ul>
                    </div>
                    <p>Use the same spelling and casing for enum values as described within the field reference CSV.</p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
